<script lang="ts" generics="T">
	import { getChatContext } from '#src/contexts/chat.context.svelte.ts';
	import { authService } from '#src/services/auth-api.service.svelte.ts';
	import ChatInput from './ChatInput.svelte';
	import UsageWarning from './UsageWarning.svelte';
	import { t } from './i18n/tranlsator.svelte';
	import { Fetchable } from '#lib/util/fetchable';
	import { globalState } from '#lib/util/global-state.svelte.ts';
	import { conversationService } from '#src/services/conversation-api.service.svelte.ts';
	import ForkIcon from 'virtual:icons/material-symbols/fork-right-rounded';
	import LockIcon from 'virtual:icons/material-symbols/lock-outline';
	import { goto } from '$app/navigation';
	import { resolveInstance } from '#lib/util/resolve-links.ts';
	import { getAttachmentsContext } from '#src/contexts/attachments.context.svelte.ts';
	const chatCtx = getChatContext();
	const attachmentsCtx = getAttachmentsContext();
	const standalone = $derived(chatCtx.standalone);
	const chatPlaceholder = $derived(chatCtx.chatPlaceholder);

	const conversationType = $derived(chatCtx.conversation?.type);
	const forkable = $derived(conversationType === 'global_briefing');
	const isAdminView = $derived(
		!!chatCtx.conversation?.owner && chatCtx.conversation.owner !== globalState.user?.id
	);
	const chatGated = $derived(
		conversationType === 'briefing' && globalState.user?.plan === 'essential'
	);
	let userInput = $state('');

	async function sendMessage() {
		if (userInput === '/logout') {
			chatCtx.fetchable.fetch(() => authService.logout());
			return;
		}

		chatCtx.fetchable.fetch(() => chatCtx.chat(userInput, attachmentsCtx?.assetRefs ?? []));
		userInput = '';
		attachmentsCtx?.clear();
	}

	async function handleAbort() {
		await chatCtx.abort();
	}

	const forkFetchable = new Fetchable();

	async function forkBriefing() {
		await forkFetchable.fetch(async () => {
			const conversation_id = await conversationService.forkBriefing(chatCtx.conversation.id);
			await goto(
				resolveInstance('/conversation/[conversationId]', { conversationId: conversation_id })
			);
		});
	}
</script>

<div class="footer">
	{#if isAdminView}
		<div class="admin-view-notice">
			{t('chat.adminView')}
		</div>
	{:else if forkable}
		<button
			class="accent large fork-button"
			onclick={forkBriefing}
			disabled={forkFetchable.fetching}
		>
			<ForkIcon />
			<span>
				{t('briefing.global.askFollowUp')}
			</span>
		</button>
	{:else if chatGated}
		<div class="chat-gate">
			<div class="chat-gate-icon"><LockIcon /></div>
			<div class="chat-gate-body">
				<strong>{t('briefing.personalized.chatGate.title')}</strong>
				<span>{t('briefing.personalized.chatGate.description')}</span>
			</div>
			<a class="button accent" href={resolveInstance('/manage-plan')}>
				{t('briefing.personalized.chatGate.cta')}
			</a>
		</div>
	{:else}
		<div class="chat-input-group">
			<ChatInput
				bind:value={userInput}
				onchange={sendMessage}
				onabort={handleAbort}
				disabled={chatCtx.fetchable.fetching}
				isStreaming={chatCtx.isStreaming}
				placeholder={chatPlaceholder}
			/>
			<UsageWarning />
		</div>
	{/if}
	<span class="disclaimer">
		{t('chat.disclaimer', {
			instanceName: globalState.instance?.name || 'Botbakery'
		})}
		{#if !standalone}
			| {t('chat.disclaimerCheckSources')}
		{/if}
		{#if standalone}
			| {t('chat.developedBy')}
			<a href="https://botbakery.berlin" target="_blank" rel="noopener noreferrer">Botbakery</a>
		{/if}
	</span>
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.footer {
		max-width: lib.$bp-sm;
		width: 100%;
		align-self: center;
		@include lib.flex-column();
		gap: 0.5rem;
		padding: 0.5rem;
		padding-top: 0;

		.chat-input-group {
			@include lib.flex-column();
		}

		.admin-view-notice {
			padding: 0.75rem 1rem;
			border-radius: 0.5rem;
			background: var(--surface-secondary);
			border: 1px solid var(--border);
			font-size: 0.875rem;
			color: var(--text-secondary);
			text-align: center;
		}

		.chat-gate {
			@include lib.flex-row();
			@include lib.flex-align(flex-start, center);
			gap: 0.75rem;
			padding: 0.75rem 1rem;
			border-radius: 0.5rem;
			background: var(--surface-secondary);
			border: 1px solid var(--border);

			.chat-gate-icon {
				font-size: 1.2rem;
				color: var(--text-secondary);
				flex-shrink: 0;
			}

			.chat-gate-body {
				@include lib.flex-column();
				gap: 0.125rem;
				flex: 1 1 0;
				min-width: 0;

				strong {
					font-size: 0.875rem;
				}

				span {
					font-size: 0.875rem;
					color: var(--text-secondary);
				}
			}
		}

		.disclaimer {
			font-size: 0.75rem;
			align-self: center;
			color: var(--text-tertiary);
			text-align: center;

			a {
				color: inherit;
				text-decoration: underline;
			}
		}
	}
</style>
